import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { COUNT_STATUS_LABELS, formatQuantityMilli, type Paginated, type StockCount, type StockCountDetail, type StockCountResult, type StockDetail, type StockItem } from '@kontor/shared';
import { api } from '../../api';
import { useSession } from '../../session';
import { InventoryTabs, LocationSelect, dateTimeFormat, errorText, milliText, parseMilli } from './shared';
import './inventory.css';

/** Inventuren (KON-166): Liste und neue Inventur für einen Lagerort oder das ganze Lager */
export function CountsPage() {
  const { can } = useSession();
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['inventory-counts'], queryFn: async () => (await api<{ data: StockCount[] }>('GET', '/inventory/counts')).data });
  const create = useMutation({
    mutationFn: () => api<{ data: StockCountDetail }>('POST', '/inventory/counts', { locationId: location || null, name: name.trim() || null }),
    onSuccess: (r) => navigate(`/lager/inventur/${r.data.id}`),
    onError: (e) => setError(errorText(e, 'Die Inventur konnte nicht angelegt werden.')),
  });
  return (
    <>
      <div className="page-head"><h1>Inventur</h1></div>
      <InventoryTabs />
      {can('inventory.write') && (
        <form className="card inv-booking" onSubmit={(e) => { e.preventDefault(); setError(null); create.mutate(); }}>
          <h2>Neue Inventur</h2>
          <p className="muted small">Der aktuelle Bestand wird als Soll festgehalten. Sie tragen die gezählten Mengen ein, beim Abschluss werden die Abweichungen gebucht.</p>
          {error && <div className="alert alert--error" role="alert">{error}</div>}
          <div className="form-grid">
            <LocationSelect value={location} onChange={setLocation} label="Lagerort (mit allen Unterorten)" allowEmpty emptyLabel="Ganzes Lager" />
            <label className="field"><span>Bezeichnung (optional)</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="Jahresinventur" /></label>
          </div>
          <div className="form-actions"><button type="submit" className="btn btn--primary" disabled={create.isPending}>{create.isPending ? 'Wird angelegt …' : 'Inventur beginnen'}</button></div>
        </form>
      )}
      <div className="card table-card">
        <table className="table table--cards">
          <thead><tr><th>Nummer</th><th>Bereich</th><th>Status</th><th className="num">Gezählt</th><th className="num">Abweichungen</th><th>Angelegt</th></tr></thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="muted">Wird geladen …</td></tr>}
            {list.data?.length === 0 && <tr><td colSpan={6} className="empty">Noch keine Inventur.</td></tr>}
            {list.data?.map((c) => (
              <tr key={c.id}>
                <td data-label="Nummer"><Link to={`/lager/inventur/${c.id}`} className="strong-link">{c.number}</Link>{c.name && <div className="muted small">{c.name}</div>}</td>
                <td data-label="Bereich">{c.locationPath ?? 'Ganzes Lager'}</td>
                <td data-label="Status">{COUNT_STATUS_LABELS[c.status]}</td>
                <td data-label="Gezählt" className="num">{c.countedCount} von {c.lineCount}</td>
                <td data-label="Abweichungen" className={`num ${c.differenceCount ? 'inv-warn' : ''}`}>{c.differenceCount}</td>
                <td data-label="Angelegt">{dateTimeFormat.format(new Date(c.createdAt))}{c.createdByName && <div className="muted small">{c.createdByName}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Eine Inventur (KON-166): gezählte Mengen eintragen, speichern, abschließen */
export function CountDetailPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const client = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const detail = useQuery({ queryKey: ['inventory-count', id], queryFn: async () => (await api<{ data: StockCountDetail }>('GET', `/inventory/counts/${id}`)).data });
  const c = detail.data;
  const open = c?.status === 'open';
  const mayWrite = can('inventory.write');
  const refresh = () => {
    for (const key of ['inventory-count', 'inventory-counts', 'inventory-stock', 'inventory-movements', 'inventory-overview', 'inventory-detail']) client.invalidateQueries({ queryKey: [key] });
  };
  const textOf = (lineId: string, counted: number | null) => values[lineId] ?? (counted === null ? '' : milliText(counted));
  const save = useMutation({
    mutationFn: async () => {
      const lines = Object.entries(values).map(([lineId, text]) => ({ id: lineId, countedMilli: text.trim() === '' ? null : parseMilli(text) }));
      if (lines.some((l) => l.countedMilli === null && values[l.id]!.trim() !== '')) throw new Error('Bitte geben Sie die gezählten Mengen als Zahl ein.');
      if (lines.length === 0) return;
      await api('PUT', `/inventory/counts/${id}/lines`, { lines });
    },
    onSuccess: () => {
      setValues({});
      refresh();
      setMessage({ ok: true, text: 'Die gezählten Mengen sind gespeichert.' });
    },
    onError: (e) => setMessage({ ok: false, text: e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const close = useMutation({
    mutationFn: async () => {
      if (Object.keys(values).length > 0) await save.mutateAsync();
      return (await api<{ data: StockCountResult }>('POST', `/inventory/counts/${id}/close`)).data;
    },
    onSuccess: (r) => {
      refresh();
      setMessage({ ok: true, text: `Die Inventur ist abgeschlossen. ${r.adjusted} Abweichungen gebucht, ${r.uncounted} Zeilen nicht gezählt und unverändert.` });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Der Abschluss hat nicht geklappt.') }),
  });
  const refreshLines = useMutation({
    mutationFn: async () => (await api<{ data: StockCountDetail }>('POST', `/inventory/counts/${id}/refresh`)).data,
    onSuccess: (r) => {
      refresh();
      setMessage({ ok: true, text: r.lines.length > (detail.data?.lines.length ?? 0) ? 'Der aktuelle Bestand ist ergänzt.' : 'Es gibt keinen Bestand, der noch fehlt.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Nachladen hat nicht geklappt.') }),
  });
  const [adding, setAdding] = useState(false);
  const cancel = useMutation({
    mutationFn: () => api('POST', `/inventory/counts/${id}/cancel`),
    onSuccess: refresh,
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Abbrechen hat nicht geklappt.') }),
  });
  if (!c) return <p className="muted">Wird geladen …</p>;

  return (
    <>
      <div className="page-head"><h1>Inventur {c.number}</h1><span className="muted">{COUNT_STATUS_LABELS[c.status]}</span></div>
      <InventoryTabs />
      <p className="muted"><Link to="/lager/inventur">← Alle Inventuren</Link> · {c.locationPath ?? 'Ganzes Lager'}{c.name ? ` · ${c.name}` : ''} · {c.countedCount} von {c.lineCount} gezählt</p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {open && mayWrite && (
        <div className="inv-actions">
          <button type="button" className="btn btn--secondary" disabled={refreshLines.isPending} onClick={() => refreshLines.mutate()}>Bestand nachladen</button>
          <button type="button" className={`btn ${adding ? 'btn--primary' : 'btn--secondary'}`} onClick={() => setAdding(!adding)}>Zeile hinzufügen</button>
        </div>
      )}
      {open && mayWrite && adding && <AddLinePanel countId={id} onDone={() => { setAdding(false); refresh(); }} />}
      <div className="card table-card">
        <table className="table table--cards">
          <thead><tr><th>Artikel</th><th>Lagerort</th><th>Charge</th><th className="num">Soll</th><th className="num">Gezählt</th><th className="num">Differenz</th></tr></thead>
          <tbody>
            {c.lines.length === 0 && <tr><td colSpan={6} className="empty">Beim Anlegen lag in diesem Bereich kein Bestand. Mit „Bestand nachladen“ übernehmen Sie, was inzwischen gebucht wurde, mit „Zeile hinzufügen“ ergänzen Sie etwas von Hand.</td></tr>}
            {c.lines.map((l) => {
              const text = textOf(l.id, l.countedMilli);
              const counted = text.trim() === '' ? null : parseMilli(text);
              const diff = counted === null ? null : counted - l.expectedMilli;
              return (
                <tr key={l.id}>
                  <td data-label="Artikel"><Link to={`/lager/bestand/${l.productId}`}>{l.productName}</Link>{l.sku && <div className="muted small">{l.sku}</div>}</td>
                  <td data-label="Lagerort">{l.locationPath}</td>
                  <td data-label="Charge">{l.batchNumber ?? '–'}</td>
                  <td data-label="Soll" className="num">{formatQuantityMilli(l.expectedMilli)} {l.unitSymbol ?? ''}</td>
                  <td data-label="Gezählt" className="num">
                    {open && mayWrite ? <input className="input inv-count-input" inputMode="decimal" aria-label={`Gezählt: ${l.productName}`} value={text} onChange={(e) => setValues({ ...values, [l.id]: e.target.value })} /> : l.countedMilli === null ? '–' : `${formatQuantityMilli(l.countedMilli)} ${l.unitSymbol ?? ''}`}
                  </td>
                  <td data-label="Differenz" className={`num ${diff ? (diff < 0 ? 'inv-bad' : 'inv-ok') : ''}`}>{diff === null ? '–' : `${diff > 0 ? '+' : ''}${formatQuantityMilli(diff)}`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {open && mayWrite && (
        <div className="form-actions">
          <button type="button" className="btn btn--secondary" disabled={save.isPending || Object.keys(values).length === 0} onClick={() => save.mutate()}>Zwischenstand speichern</button>
          <button type="button" className="btn btn--primary" disabled={close.isPending} onClick={() => window.confirm('Inventur abschließen? Die Abweichungen werden als Bewegungen gebucht, nicht gezählte Zeilen bleiben unverändert.') && close.mutate()}>Abschließen und buchen</button>
          <button type="button" className="btn btn--danger" disabled={cancel.isPending} onClick={() => window.confirm('Inventur abbrechen? Es wird nichts gebucht.') && cancel.mutate()}>Abbrechen</button>
        </div>
      )}
    </>
  );
}

/** Zeile für etwas ergänzen, das beim Anlegen nicht im Bestand stand, zum Beispiel ein Fund im Regal */
function AddLinePanel({ countId, onDone }: { countId: string; onDone: () => void }) {
  const [product, setProduct] = useState('');
  const [location, setLocation] = useState('');
  const [batch, setBatch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const products = useQuery({ queryKey: ['inventory-stock-options'], queryFn: async () => (await api<Paginated<StockItem>>('GET', '/inventory/stock?perPage=100&sort=name')).data, staleTime: 30_000 });
  const item = products.data?.find((p) => p.productId === product);
  const detail = useQuery({ queryKey: ['inventory-detail', product], queryFn: async () => (await api<{ data: StockDetail }>('GET', `/inventory/stock/${product}`)).data, enabled: !!item?.batchTracking });
  const add = useMutation({
    mutationFn: () => api('POST', `/inventory/counts/${countId}/lines`, { productId: product, locationId: location, batchId: item?.batchTracking ? batch || null : null }),
    onSuccess: onDone,
    onError: (e) => setError(errorText(e, 'Die Zeile konnte nicht ergänzt werden.')),
  });
  return (
    <form className="card inv-booking" onSubmit={(e) => { e.preventDefault(); setError(null); add.mutate(); }} aria-label="Zeile hinzufügen">
      <h2>Zeile hinzufügen</h2>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Artikel</span>
          <select className="input" value={product} onChange={(e) => { setProduct(e.target.value); setBatch(''); }} required>
            <option value="">Bitte wählen</option>
            {products.data?.map((p) => <option key={p.productId} value={p.productId}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>)}
          </select>
        </label>
        <LocationSelect value={location} onChange={setLocation} label="Lagerort" required />
        {item?.batchTracking && (
          <label className="field">
            <span>Charge</span>
            <select className="input" value={batch} onChange={(e) => setBatch(e.target.value)} required>
              <option value="">Bitte wählen</option>
              {detail.data?.batches.map((b) => <option key={b.id} value={b.id}>{b.number}</option>)}
            </select>
          </label>
        )}
      </div>
      <div className="form-actions"><button type="submit" className="btn btn--primary" disabled={add.isPending}>Hinzufügen</button></div>
    </form>
  );
}
